import React, { createContext, useCallback, useContext, useRef, useState } from 'react';
import Joyride, { STATUS } from 'react-joyride';
import { localize } from '@revbox/localization'
import { getPathKey, useCookiesContext } from './CookiesContext';
import { editUser } from '../services/micro';
import Cookies from 'universal-cookie';
import { buildSteps, targets } from './utils/config.joyride';
import { cookiesKeyNames } from '@revbox/common-fe'

const JoyrideContext = createContext();

export const useJoyride = () => {
  const context = useContext(JoyrideContext);
  if (!context) throw new Error('useJoyride must be inside JoyrideProvider');
  return context;
};

export const JoyrideProvider = ({ children }) => {
  const [runningTours, setRunningTours] = useState({});
  const [stepIndexesByTour, setStepIndexesByTour] = useState({});
  // should be true if tours were triggered via button in header
  const [isTourTriggered, setIsTourTriggered] = useState(false);

  const stepsRef = useRef({});
  const { getLanguageAbbr, updateCookiesProperty, getAdvertiserId, getIsTourCompleted } = useCookiesContext();
  const language = getLanguageAbbr();
  const isIframed = window !== window.parent;
  const urlParams = new URLSearchParams(window.location.search)
  const publisherId = urlParams.get('pub') || 0;

  const initialTour = cookiesKeyNames.tourAdvInit;
  const biddingTour = cookiesKeyNames.tourAdvBidding;

  const getUserId = () => {
    const cookies = new Cookies();
    const userIdFromCookies = cookies.get(getPathKey())?.[cookiesKeyNames.userId];
    const userIdFromLS = localStorage.getItem(getPathKey())?.[cookiesKeyNames.userId];
    return userIdFromCookies || userIdFromLS;
  }

  const handleCallback = useCallback(
    (tour_name) => ({ action, status, index, type }) => {
      const isLastStep = stepIndexesByTour[tour_name] === (stepsRef.current[tour_name]?.length || 0);
      const isTourFinished = ['skip', 'close', 'stop', 'error'].includes(action) || isLastStep || status === STATUS.FINISHED;

      if (index === 7 && type === 'error:target_not_found') {
        console.log('target not found');
        jumpToBiddingRulesStep();
      }

      if (isTourFinished) {
        completeTour(tour_name)
          .then(() => {
            console.log(`Tour ${tour_name} completed`)
          })
          .catch((err) => {
            console.error('Error completing tour:', err);
          });
        return;
      }

      if (type === 'step:after') {
        const isMobile = window.innerWidth < 992;

        if (tour_name === initialTour && isMobile) {
          if (index === 1 && action === 'prev') {
            document.body.classList.remove('sidebar-show');
          }

          if (index === 0 && action === 'next') {
            document.body.classList.add('sidebar-show');
          }

          const stepsLength = stepsRef.current[tour_name]?.length || 0;
          const isPreLastStep = index === stepsLength - 2;

          if (isPreLastStep && action === 'next') {
            document.body.classList.remove('sidebar-show');
          }

          if (isPreLastStep && action === 'prev') {
            document.body.classList.add('sidebar-show');
          }
        }

        setStepIndexesByTour((prev) => ({
          ...prev,
          [tour_name]: action === 'next' ? index + 1 : index - 1,
        }));
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [stepIndexesByTour, isTourTriggered],
  );

  const startTour = useCallback((tourKey) => {
    const isTourCompleted = getIsTourCompleted(tourKey);

    if (isTourCompleted && !isTourTriggered) return;

    setRunningTours((prev) => ({ ...prev, [tourKey]: true }));
    stepsRef.current[tourKey] = buildSteps(language)[tourKey];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [language, isTourTriggered]);


  const stopTour = useCallback((tourKey) => {
    setRunningTours((prev) => ({ ...prev, [tourKey]: false }));
    setStepIndexesByTour((prev) => ({ ...prev, [tourKey]: 0 }));
  }, []);

  const triggerTour = useCallback(() => {
    stopTour(initialTour)
    stopTour(biddingTour)

    const userId = getUserId();
    editUser(userId, {
      [initialTour]: false,
      [biddingTour]: false,
    })
      .then(() => {
        updateCookiesProperty(initialTour, 'false', getAdvertiserId());
        updateCookiesProperty(biddingTour, 'false', getAdvertiserId());
      })
      .catch((err) => {
        console.error('Error saving tour completion:', err);
      });
    setIsTourTriggered(true)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const completeTour = async (tourKey) => {
    const userId = getUserId();
    stopTour(tourKey);
    updateCookiesProperty(tourKey, 'true', getAdvertiserId());
    await editUser(userId, {
      [tourKey]: true,
    })
      .catch((err) => console.error('Error saving tour completion:', err));
  };

  const handleChangePage = (tourKey) => {
    if (tourKey === initialTour && stepIndexesByTour.tour_adv_init === 1) {
      setStepIndexesByTour((prev) => ({ ...prev, [tourKey]: 3 }));
    }
  }

  const startPPCPageTourAfterPageChange = useCallback(() => {
    if (stepIndexesByTour[initialTour] === 2) {
      setRunningTours((prev) => ({ ...prev, [initialTour]: true }))
    }
  }, [initialTour])

  const jumpToBiddingRulesStep = () => {
    const currentTourSteps = stepsRef.current[initialTour];

    if (!currentTourSteps) return;
    const index = currentTourSteps.findIndex((step) => step.target.includes(targets.ppcBiddingRules));
    setStepIndexesByTour((prev) => ({ ...prev, [initialTour]: index }));
    setRunningTours((prev) => ({ ...prev, [initialTour]: true }));
  }

  const isInitialTourRunning = useCallback(() => {
    return runningTours[initialTour] || false;
  } , [runningTours, initialTour])

  if ((publisherId == 67 || publisherId == 10) || isIframed) {
    return (
      <JoyrideContext.Provider value={{ startTour, isInitialTourRunning, handleChangePage, stepIndexes: stepIndexesByTour, stopTour, triggerTour, isTourTriggered, setRuns: setRunningTours, startPPCPageTourAfterPageChange }}>
        {children}
      </JoyrideContext.Provider>
    );
  }

  return (
    <JoyrideContext.Provider value={{ startTour, handleChangePage, stepIndexes: stepIndexesByTour, stopTour, triggerTour, isTourTriggered, setRuns: setRunningTours, jumpToBiddingRulesStep, startPPCPageTourAfterPageChange, isInitialTourRunning }}>
      {children}
      {Object.keys(runningTours).map((tourKey) => (
        <Joyride
          key={tourKey}
          run={runningTours[tourKey]}
          steps={stepsRef.current[tourKey] || []}
          stepIndex={stepIndexesByTour[tourKey] || 0}
          scrollToFirstStep
          callback={handleCallback(tourKey)}
          continuous
          showProgress
          showSkipButton
          disableScrolling
          styles={{ options: { zIndex: 9999 } }}
          locale={localize(language)}
        />
      ))}
    </JoyrideContext.Provider>
  );
};
